import { useRef, useState } from "react";
import { Chip, Label, Panel, Reveal } from "../components/primitives";
import { CopyCA } from "../components/CopyCA";

const SECTIONS = [
  {
    id: "introduction", title: "Introduction",
    body: [
      "SYNAPTIX is the neural coordination layer for autonomous finance. It connects specialized AI agents with onchain financial infrastructure — capital, vaults, execution venues and risk systems — inside one coherent protocol surface.",
      "The premise is simple: humans define the mandate, AI agents analyze and coordinate, risk systems enforce constraints, and smart contracts execute. Onchain capital becomes autonomous without becoming unaccountable.",
      "SYNAPTIX is not an AI trading bot. It is an autonomous finance infrastructure layer, built for the Robinhood Chain ecosystem.",
    ],
  },
  {
    id: "architecture", title: "Architecture",
    body: [
      "The protocol is organized as a vertical pipeline. Intent enters at the top; execution exits at the bottom. Every layer is observable and every transition is permissioned.",
    ],
    code: `Human
  -> Mandate          (intent + constraints)
  -> Agent            (analysis + proposals)
  -> Guardian         (risk validation)
  -> Vault            (capital custody)
  -> Execution        (onchain settlement)`,
  },
  {
    id: "agents", title: "Agents",
    body: [
      "Agents are specialized, narrow intelligence units. An ANALYST researches. A TRADER proposes. A GUARDIAN constrains. An EXECUTOR routes. No single agent holds end-to-end authority.",
      "Each agent is registered in the Agent Registry with declared capabilities, risk tier and permission envelope. Agents can be composed into strategies but can never exceed their envelope.",
    ],
    code: `agent = {
  id: "GUARDIAN-11",
  role: "risk",
  capabilities: ["exposure", "drawdown", "limits"],
  permissions: { max_position: 0.20, max_trade: 500 }
}`,
  },
  {
    id: "vaults", title: "Vaults",
    body: [
      "SYNAPTIX Vaults are programmable capital containers. Each vault declares its mandate, its managing agents and its Guardian constraints. Capital enters with permissions attached; it cannot be deployed outside them.",
      "Vault contracts are not yet deployed. Interfaces on this site marked DEMO show intended behavior only.",
    ],
    status: "NOT DEPLOYED",
  },
  {
    id: "guardian", title: "Guardian",
    body: [
      "Guardian is the risk supervision layer. Every proposed action — rebalance, entry, exit, transfer — is validated against position limits, trade size limits, drawdown bounds and slippage tolerances before it can reach a vault.",
      "Guardian decisions are deterministic given the mandate: APPROVE, SCALE DOWN, or REJECT. The decision and its reasoning are logged to agent memory.",
    ],
    code: `guardian.check(proposal)
  -> exposure_ok   : PASS
  -> position_ok   : PASS
  -> drawdown_ok   : PASS
  -> slippage_ok   : PASS
  => APPROVED (simulation)`,
  },
  {
    id: "memory", title: "Memory",
    body: [
      "Memory is the persistent intelligence layer: portfolio state, agent decisions, Guardian verdicts and regime observations, stored as a shared context every agent can read.",
      "Memory turns isolated agent outputs into compounding institutional knowledge. It is what makes the layer neural rather than merely automated.",
    ],
    status: "NOT DEPLOYED",
  },
  {
    id: "terminal", title: "Terminal",
    body: [
      "The Terminal is the unified command center: agent control, portfolio intelligence, Guardian monitoring and the activity log in one surface.",
      "Today the Terminal runs in SIMULATION MODE. Live data and onchain execution tabs activate as the corresponding infrastructure ships.",
    ],
  },
  {
    id: "execution", title: "Execution",
    body: [
      "The execution layer routes Guardian-approved actions to onchain infrastructure on Robinhood Chain — vault interactions, swaps and settlement — with slippage control and full attribution.",
      "No contracts are currently deployed. No transactions can be initiated from this website.",
    ],
    status: "NOT DEPLOYED",
  },
  {
    id: "security", title: "Security",
    body: [
      "Security is structural, not cosmetic: contract-enforced permissions, separation of duties between agents, deterministic risk validation, and complete audit logs.",
      "Smart contracts will undergo independent review before any deployment. No audit has been completed or claimed at this stage.",
    ],
  },
  {
    id: "prices", title: "Price Intelligence",
    body: [
      "REFERENCE_PRICE: USD quotes for the underlying instrument from the Robinhood quotes API (public endpoint, venue timestamps). ONCHAIN_PRICE: computed from actual pool reserves on Robinhood Chain — currently the SYNX/WETH pool (reserves verified against onchain balanceOf). The two are never merged; a token can trade at a premium or discount to its reference.",
      "Corporate actions: every token's currentMultiplier is stored with source and timestamp. Robinhood has not published an authoritative definition of the multiplier's semantics, so adjusted prices are never computed — all prices are RAW. If semantics become officially documented, normalization activates behind a versioned formula.",
      "Freshness: an observation is VERIFIED when its venue timestamp is within the staleness budget (default 24h), STALE beyond it (markets close overnight and weekends), REVIEW when a move exceeds the configured threshold (default 20%) — flagged, never discarded. Invalid values are rejected before storage.",
      "History: observations persist in MongoDB with a uniqueness constraint on (token, source, sourceTimestamp, priceType) — re-ingestion is idempotent. Candles are derived from real stored ticks only; empty intervals are omitted, never interpolated. Coverage is PARTIAL from deployment time; the quotes API offers no historical backfill and the public RPC is not an archive node.",
      "Quality states: VERIFIED · STALE · REVIEW · DISPUTED (multi-provider disagreement) · UNAVAILABLE. Every price response carries sourceId, sourceTimestamp, receivedAt, quoteCurrency, priceType and quality.",
    ],
    code: `GET /api/prices                 -> all latest observations + quality metrics
GET /api/prices/AAPL            -> reference + onchain + corporate action
GET /api/prices/AAPL/history    -> raw observations (bounded, paginated)
GET /api/prices/AAPL/candles?interval=15m
GET /api/prices/providers/status
GET /api/corporate-actions/CRM`,
  },
  {
    id: "roadmap", title: "Roadmap",
    body: [
      "PHASE 01 — FOUNDATION: brand, architecture, terminal, agent framework. IN DEVELOPMENT.",
      "PHASE 02 — INTELLIGENCE: agent registry, portfolio brain, guardian, memory. PLANNED.",
      "PHASE 03 — CAPITAL: vaults, strategy engine, execution layer. PLANNED.",
      "PHASE 04 — AUTONOMY: multi-agent treasury, autonomous rebalancing, cross-protocol coordination. RESEARCH.",
    ],
  },
  {
    id: "faq", title: "FAQ",
    body: [
      "Is SYNAPTIX live? The terminal, agents and vaults on this site operate in simulation. Live execution is not available.",
      "Is there a token? Yes — CA: 0xaba1…499b (full address and copy button on the homepage). Always verify the address through official channels before interacting.",
      "Is SYNAPTIX affiliated with Robinhood? SYNAPTIX is built for the Robinhood Chain ecosystem. No official affiliation is claimed.",
      "Is anything here financial advice? No. All diagnostics, signals and simulations are demonstrations of protocol behavior.",
    ],
  },
];

export default function Docs() {
  const [active, setActive] = useState("introduction");
  const refs = useRef({});

  const jump = (id) => {
    setActive(id);
    refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="pt-16" data-testid="docs-page">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-12 md:px-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="sticky top-24 border border-white/[0.08] bg-panel p-3" data-testid="docs-sidebar">
            <Label className="px-2 py-2">Documentation</Label>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => jump(s.id)}
                data-testid={`docs-nav-${s.id}`}
                className={`block w-full border-l-2 px-3 py-2 text-left font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                  active === s.id ? "border-pulse bg-pulse/[0.06] text-pulse-soft" : "border-transparent text-fog-400 hover:text-fog-100"}`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-9">
          <Reveal>
            <div className="mb-10 border-b border-white/[0.08] pb-8">
              <Label tone="green" className="mb-3">Protocol Documentation</Label>
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-fog-100 sm:text-4xl">SYNAPTIX DOCS</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-fog-400">
                Technical reference for the neural layer — architecture, agents, vaults, risk and execution.
              </p>
            </div>
          </Reveal>

          <div className="space-y-14">
            {SECTIONS.map((s) => (
              <section
                key={s.id}
                ref={(el) => (refs.current[s.id] = el)}
                className="scroll-mt-24"
                data-testid={`docs-section-${s.id}`}
              >
                <div className="mb-4 flex items-center gap-4">
                  <h2 className="font-display text-xl font-bold tracking-wide text-fog-100 sm:text-2xl">{s.title}</h2>
                  {s.status && <Chip tone="gold">{s.status}</Chip>}
                </div>
                {s.body.map((p, i) => (
                  <p key={i} className="mb-3 max-w-3xl text-sm leading-relaxed text-fog-400">{p}</p>
                ))}
                {s.code && (
                  <pre className="mt-4 overflow-x-auto border border-white/[0.08] bg-ink-900 p-4 font-mono text-xs leading-relaxed text-pulse-soft/90">
                    {s.code}
                  </pre>
                )}
              </section>
            ))}
          </div>

          <Panel className="mt-14 p-5" data-testid="docs-contract-status">
            <Label className="mb-3" tone="gold">Contract Status</Label>
            <div className="grid gap-px bg-white/[0.08] sm:grid-cols-3">
              {["SYNAPTIX CORE", "VAULT ENGINE"].map((c) => (
                <div key={c} className="bg-ink-900 p-4">
                  <span className="font-mono text-[11px] tracking-[0.15em] text-fog-100">{c}</span>
                  <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-brass-bright">Not Deployed</span>
                </div>
              ))}
              <div className="bg-ink-900 p-4">
                <span className="font-mono text-[11px] tracking-[0.15em] text-fog-100">TOKEN / CA</span>
                <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-pulse-soft">Live</span>
                <CopyCA testId="docs-copy-ca" className="mt-1.5" />
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </main>
  );
}
